import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Play, BookOpen, User, Calendar, Headphones, Search, Filter, X } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Sermons() {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [speakerFilter, setSpeakerFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sermons");
        setSermons(data);
        if (data.length > 0) setActive(data[0]);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const speakers = useMemo(() => {
    const s = new Set(sermons.map((x) => x.speaker).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [sermons]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sermons.filter((s) => {
      if (speakerFilter !== "all" && s.speaker !== speakerFilter) return false;
      if (!q) return true;
      return (
        s.title?.toLowerCase().includes(q) ||
        s.speaker?.toLowerCase().includes(q) ||
        s.scripture?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    });
  }, [sermons, query, speakerFilter]);

  // Update active if it's filtered out
  useEffect(() => {
    if (active && !filtered.find((s) => s.id === active.id) && filtered.length > 0) {
      setActive(filtered[0]);
    }
  }, [filtered, active]);

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="sermons-page">
      <Seo
        title="Sermons"
        description="Watch and listen to spirit-filled messages from Pastor Demetrial Donshae Hill and The Push Center ministry team."
        path="/sermons"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Messages & Teachings
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            The <span className="italic text-electric text-pulse">Word.</span> Unfiltered.
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Practical, spirit-filled teaching from Pastor Demetrial Donshae Hill and our ministry.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24">
        {/* SEARCH + FILTER */}
        <div className="mb-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="search"
              placeholder="Search by title, speaker, scripture..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-3 rounded-full bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition"
              data-testid="sermons-search"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                data-testid="sermons-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {speakers.length > 1 && (
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <select
                value={speakerFilter}
                onChange={(e) => setSpeakerFilter(e.target.value)}
                className="pl-11 pr-6 py-3 rounded-full bg-[#0a0a0a] border border-white/10 text-white focus:outline-none focus:border-electric appearance-none cursor-pointer min-w-[200px]"
                data-testid="sermons-filter-speaker"
              >
                {speakers.map((s) => (
                  <option key={s} value={s}>{s === "all" ? "All Speakers" : s}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-32 flex justify-center" data-testid="sermons-loading">
            <div className="w-10 h-10 border-2 border-electric border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sermons.length === 0 ? (
          <div className="py-24 text-center" data-testid="sermons-empty">
            <BookOpen className="w-12 h-12 text-electric mx-auto mb-5" />
            <h3 className="font-display font-bold text-2xl mb-3">Sermons coming soon</h3>
            <p className="text-zinc-400 max-w-md mx-auto">
              We're preparing to share recent messages with you. Visit us in person at 9:00 AM Sunday.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" data-testid="sermons-no-results">
            <Search className="w-10 h-10 text-electric mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No results</h3>
            <p className="text-zinc-400 text-sm">Try a different search or filter.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {active && (
                <div className="rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5" data-testid="featured-sermon">
                  {active.video_url ? (
                    <div className="aspect-video bg-black">
                      <iframe
                        title={active.title}
                        src={active.video_url.replace("watch?v=", "embed/")}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : active.thumbnail_url ? (
                    <div className="aspect-video relative">
                      <img src={active.thumbnail_url} alt={active.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Play className="w-16 h-16 text-electric" />
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-electric/20 to-black flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-electric opacity-40" />
                    </div>
                  )}
                  <div className="p-8">
                    <div className="flex items-center gap-4 text-xs text-electric font-bold uppercase tracking-wider mb-3">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(active.sermon_date)}</span>
                      <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{active.speaker}</span>
                    </div>
                    <h2 className="font-display font-black text-3xl tracking-tight mb-3">{active.title}</h2>
                    {active.scripture && (
                      <div className="text-electric italic mb-4 text-sm">{active.scripture}</div>
                    )}
                    {active.description && (
                      <p className="text-zinc-300 leading-relaxed">{active.description}</p>
                    )}
                    {active.audio_url && (
                      <a
                        href={active.audio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 inline-flex items-center gap-2 text-electric font-bold hover:gap-3 transition-all"
                        data-testid="sermon-audio-link"
                      >
                        <Headphones className="w-4 h-4" /> Listen to audio
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-4">
                {filtered.length} {filtered.length === 1 ? "Message" : "Messages"}
              </div>
              <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2">
                {filtered.map((s, i) => (
                  <motion.button
                    key={s.id}
                    onClick={() => setActive(s)}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      active?.id === s.id
                        ? "border-electric/50 bg-electric/5"
                        : "border-white/5 bg-[#0a0a0a] hover:border-electric/30"
                    }`}
                    data-testid={`sermon-list-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{formatDate(s.sermon_date)} · {s.speaker}</div>
                    <div className="font-display font-bold text-base leading-tight">{s.title}</div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
